import type { DenyReason, FieldKeyString, ServerMessage, Uuid } from '@colplat/protocol';
import type { Transport, TransportState } from './transport.js';

/**
 * This tab's hold — ADR-0003 as amended 2026-10-22.
 *
 * A hold has no expiry. It ends when this tab releases it (a save, Cancel,
 * closing the form), when the page itself goes away, or when somebody takes
 * it over. It does NOT end because the connection dropped, the tab was
 * hidden or the device slept: the server keeps it, marked offline, and this
 * tab picks it up again with its token when the connection comes back
 * (`claim` with `resume`). The token lives in the tab's `sessionStorage`
 * (`HoldTokens`), so a tab the browser discarded and restored resumes too.
 *
 * Everything the old client did to survive lapses — the renew heartbeat
 * paused while hidden, the disconnected-window deadline, escalating a failed
 * renew into a re-claim, remembering which key was still wanted — is gone,
 * because there is no lapse left to survive.
 *
 * A client holds at most one key at a time — focus is singular.
 */
export type LeaseState =
  | { phase: 'idle' }
  | { phase: 'claiming'; key: FieldKeyString }
  | { phase: 'held'; key: FieldKeyString; leaseId: Uuid; since: string }
  /** Held, with no connection right now. Still ours; resumed on reconnect. */
  | { phase: 'disconnected'; key: FieldKeyString; leaseId: Uuid; since: string }
  | { phase: 'releasing'; key: FieldKeyString; leaseId: Uuid };

export type LeaseOutcome =
  | { ok: true }
  /**
   * `deniedBecause` carries the server's own reason. `'held'` means someone
   * (another tab of the same person included) holds it, and `since`/`online`
   * describe them; `'no-right'` means this connection may not hold this key
   * at all — the server then echoes the requester's own id as `holder`, so a
   * caller must read the reason before trusting the holder.
   */
  | { ok: false; reason: 'denied'; deniedBecause: DenyReason; holder: Uuid; since?: string; online?: boolean }
  | { ok: false; reason: 'offline' }
  | { ok: false; reason: 'superseded' };

/**
 * Where a tab keeps its hold tokens. `sessionStorage` in a browser: per tab,
 * survives a reload and a discard, gone with the tab — the holder's lifetime.
 */
export interface HoldTokens {
  get(key: FieldKeyString): Uuid | undefined;
  set(key: FieldKeyString, token: Uuid): void;
  delete(key: FieldKeyString): void;
}

export function memoryHoldTokens(): HoldTokens {
  const map = new Map<FieldKeyString, Uuid>();
  return { get: (k) => map.get(k), set: (k, t) => { map.set(k, t); }, delete: (k) => { map.delete(k); } };
}

export interface LeaseClientOptions {
  transport: Pick<Transport, 'send' | 'onMessage' | 'onStateChange'>;
  /** Defaults to an in-memory store — fine for tests, wrong for a page (see `sessionHoldTokens`). */
  tokens?: HoldTokens;
  /**
   * Release on the way out of the page, by a route that outlives it — the
   * app's `keepalive` request to `…/holds/release`. The socket's own
   * "going away" close releases too (services/sync); either is enough.
   */
  releaseOnExit?: (key: FieldKeyString, token: Uuid) => void;
  /** Where `pagehide` comes from. Injectable for tests; the window otherwise. */
  pageEvents?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;
}

export class LeaseClient {
  private _state: LeaseState = { phase: 'idle' };
  private readonly listeners = new Set<(s: LeaseState) => void>();
  private pendingClaim: {
    key: FieldKeyString;
    resolve: (o: LeaseOutcome) => void;
    /** Handed back to a concurrent caller for the same key rather than starting a second claim. */
    promise: Promise<LeaseOutcome>;
  } | undefined;
  /**
   * Claims given up before their answer came — the form closed, or another
   * key was claimed. If the server grants one anyway it is released at once:
   * a hold nobody tracks would never lapse now, and would sit on the record
   * as somebody's "unfinished" edit until taken over.
   */
  private readonly abandoned = new Set<FieldKeyString>();
  private readonly unsubscribers: Array<() => void> = [];
  private readonly tokens: HoldTokens;

  constructor(private readonly opts: LeaseClientOptions) {
    this.tokens = opts.tokens ?? memoryHoldTokens();
    this.unsubscribers.push(opts.transport.onMessage((msg) => this.onMessage(msg)));
    this.unsubscribers.push(opts.transport.onStateChange((s) => this.onTransportStateChange(s)));
    const page = opts.pageEvents ?? (typeof window !== 'undefined' ? window : undefined);
    if (page) {
      const onExit = (): void => this.onPageExit();
      page.addEventListener('pagehide', onExit);
      this.unsubscribers.push(() => page.removeEventListener('pagehide', onExit));
    }
  }

  get state(): LeaseState { return this._state; }

  subscribe(fn: (s: LeaseState) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Claim a key, at most once at a time — or pick up this tab's own hold of
   * it again, if it still has the token.
   *
   * The de-duplication is load-bearing: `setState` notifies subscribers
   * synchronously, and a subscriber reacting to a hold change by claiming
   * (which is exactly what an open editor does) re-enters this method before
   * it has returned. Two claims for one key reaching the server would see the
   * second refused by the first. A claim for a *different* key supersedes the
   * one in flight, and that caller is told so rather than left waiting.
   */
  async claim(key: FieldKeyString): Promise<LeaseOutcome> {
    return this.acquire(key, 'claim');
  }

  /**
   * Take a key someone else is holding. Same bookkeeping as a claim; the
   * consequences live on the server (`handleSteal`) and in the form that has
   * to explain itself to the person who lost it.
   */
  async steal(key: FieldKeyString): Promise<LeaseOutcome> {
    return this.acquire(key, 'steal');
  }

  private async acquire(key: FieldKeyString, kind: 'claim' | 'steal'): Promise<LeaseOutcome> {
    const current = this._state;
    // Already ours: nothing to ask. A form reopened on the record this tab
    // holds — after a reload, say — just carries on.
    if (kind === 'claim' && (current.phase === 'held' || current.phase === 'disconnected') && current.key === key) {
      return { ok: true };
    }
    const inFlight = this.pendingClaim;
    if (inFlight) {
      if (inFlight.key === key && kind === 'claim') return inFlight.promise;
      this.abandon(inFlight);
    }
    if ((current.phase === 'held' || current.phase === 'disconnected') && current.key !== key) {
      await this.release(current.key);
    }
    return this.send(key, kind);
  }

  private send(key: FieldKeyString, kind: 'claim' | 'steal'): Promise<LeaseOutcome> {
    let settle: (o: LeaseOutcome) => void = () => {};
    const promise = new Promise<LeaseOutcome>((resolve) => { settle = resolve; });
    // Registered before any `setState`, so a subscriber that re-enters
    // `claim` synchronously finds this claim already in flight.
    this.pendingClaim = { key, resolve: settle, promise };

    const resume = kind === 'claim' ? this.tokens.get(key) : undefined;
    if (this._state.phase !== 'disconnected') this.setState({ phase: 'claiming', key });
    const sent = this.opts.transport.send(kind === 'steal' ? { t: 'steal', key } : { t: 'claim', key, ...(resume ? { resume } : {}) });
    if (!sent) {
      this.pendingClaim = undefined;
      if (this._state.phase === 'claiming') this.setState({ phase: 'idle' });
      settle({ ok: false, reason: 'offline' });
    }
    return promise;
  }

  /**
   * Done with it. Over the socket when there is one; otherwise by the exit
   * route, so a form closed while offline does not leave its hold standing
   * as somebody's "unfinished" edit for days.
   */
  async release(key: FieldKeyString): Promise<void> {
    const pending = this.pendingClaim;
    if (pending?.key === key) {
      this.abandon(pending);
      if (this._state.phase === 'claiming') this.setState({ phase: 'idle' });
    }
    const current = this._state;
    if ((current.phase !== 'held' && current.phase !== 'disconnected') || current.key !== key) {
      this.tokens.delete(key);
      return;
    }
    const sent = this.opts.transport.send({ t: 'release', key, leaseId: current.leaseId });
    if (!sent) this.opts.releaseOnExit?.(key, current.leaseId);
    this.tokens.delete(key);
    this.setState({ phase: 'idle' });
  }

  dispose(): void {
    for (const unsub of this.unsubscribers) unsub();
  }

  private abandon(pending: NonNullable<LeaseClient['pendingClaim']>): void {
    this.pendingClaim = undefined;
    this.abandoned.add(pending.key);
    pending.resolve({ ok: false, reason: 'superseded' });
  }

  private setState(s: LeaseState): void {
    this._state = s;
    for (const fn of this.listeners) fn(s);
  }

  /** Leaving the page releases (ADR-0003, 2026-10-22). Not the token: a discarded tab never gets here, and keeps it. */
  private onPageExit(): void {
    const current = this._state;
    if (current.phase !== 'held' && current.phase !== 'disconnected') return;
    this.opts.releaseOnExit?.(current.key, current.leaseId);
    this.tokens.delete(current.key);
  }

  private onMessage(msg: ServerMessage): void {
    if (msg.t === 'granted') {
      if (this.abandoned.delete(msg.key) && this.pendingClaim?.key !== msg.key) {
        this.opts.transport.send({ t: 'release', key: msg.key, leaseId: msg.leaseId });
        return;
      }
      const pending = this.pendingClaim;
      if (pending && pending.key === msg.key) {
        this.pendingClaim = undefined;
        this.tokens.set(msg.key, msg.leaseId);
        this.setState({ phase: 'held', key: msg.key, leaseId: msg.leaseId, since: msg.since });
        pending.resolve({ ok: true });
      }
      return;
    }

    if (msg.t === 'denied') {
      this.abandoned.delete(msg.key);
      const pending = this.pendingClaim;
      if (pending && pending.key === msg.key) {
        this.pendingClaim = undefined;
        const current = this._state;
        // A resume refused: taken over while this tab was away. The token is
        // dead; the server also sent `leaseTaken` with `stolenFrom`, which is
        // what the form reacts to.
        if (current.phase === 'disconnected' && current.key === msg.key) this.tokens.delete(msg.key);
        this.setState({ phase: 'idle' });
        pending.resolve({
          ok: false, reason: 'denied', deniedBecause: msg.reason, holder: msg.holder,
          ...(msg.since !== undefined ? { since: msg.since } : {}),
          ...(msg.online !== undefined ? { online: msg.online } : {}),
        });
      }
      return;
    }

    // Taken over: from now on it is somebody else's, and this tab's token is
    // dead. A `leaseTaken` without `stolenFrom` is somebody's claim or their
    // coming back online — about a key this tab does not hold.
    if (msg.t === 'leaseTaken' && msg.stolenFrom !== undefined) {
      const current = this._state;
      if ((current.phase === 'held' || current.phase === 'disconnected') && current.key === msg.key) {
        this.tokens.delete(msg.key);
        this.setState({ phase: 'idle' });
      }
      return;
    }

    // Released through `api` — the save that carried this hold's token.
    if (msg.t === 'leaseFreed') {
      const current = this._state;
      if ((current.phase === 'held' || current.phase === 'disconnected') && current.key === msg.key) {
        this.tokens.delete(msg.key);
        this.setState({ phase: 'idle' });
      }
    }
  }

  private onTransportStateChange(s: TransportState): void {
    const current = this._state;
    if (s === 'open') {
      // Back: pick the hold up again with its token. The server keeps it
      // offline meanwhile, so nobody else has walked in — unless they took it
      // over, in which case this is refused and the form is told.
      if (current.phase === 'disconnected') void this.send(current.key, 'claim');
      return;
    }
    if (current.phase === 'held') {
      this.setState({ phase: 'disconnected', key: current.key, leaseId: current.leaseId, since: current.since });
    } else if (current.phase === 'claiming') {
      // A claim in flight when the socket drops cannot be granted.
      const pending = this.pendingClaim;
      this.pendingClaim = undefined;
      this.setState({ phase: 'idle' });
      pending?.resolve({ ok: false, reason: 'offline' });
    }
  }
}

/**
 * The tab's hold tokens in `sessionStorage`, under a prefix. Use only once the
 * tab is known not to be a duplicate (`claimTabIdentity`), or a duplicated
 * tab would resume its original's holds.
 */
export function sessionHoldTokens(storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | undefined): HoldTokens {
  if (!storage) return memoryHoldTokens();
  const k = (key: FieldKeyString): string => `colplat:hold:${key}`;
  return {
    get: (key) => { try { return storage.getItem(k(key)) ?? undefined; } catch { return undefined; } },
    set: (key, token) => { try { storage.setItem(k(key), token); } catch { /* storage off: in-page only */ } },
    delete: (key) => { try { storage.removeItem(k(key)); } catch { /* storage off */ } },
  };
}
